import { useState, useRef, useEffect } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const SUPABASE_BUCKET = process.env.REACT_APP_SUPABASE_STORAGE_BUCKET;
const SUPABASE_PATH = process.env.REACT_APP_SUPABASE_STORAGE_PATH;

export const useRecording = (url) => {
  const [recording, setRecording] = useState(false);
  const [canStop, setCanStop] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [recordError, setRecordError] = useState('');
  const [videos, setVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  
  const recordStartTimeRef = useRef(0);
  const recordedChunksRef = useRef([]);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);

  // Fetch videos for the current URL from database
  const fetchVideos = async (targetUrl) => {
    if (!targetUrl) return;
    
    setLoadingVideos(true);
    try {
      const urlObj = new URL(targetUrl);
      const domain = urlObj.hostname.replace(/\./g, '-');
      
      console.log('=== VIDEO FETCHING DEBUG ===');
      console.log('Target URL:', targetUrl);
      console.log('Domain:', urlObj.hostname);
      console.log('Domain folder:', domain);
      
      // Query the screen_recordings table for videos matching the domain
      const response = await fetch(`${SUPABASE_URL}/rest/v1/screen_recordings?domain=eq.${domain}&select=*`, {
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': `Bearer ${SUPABASE_KEY}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Database response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Database response data:', data);
        
        const videoFiles = data.map(record => ({
          name: record.file_path,
          url: `${SUPABASE_URL}/storage/v1/object/public/${SUPABASE_BUCKET}/${record.file_path}`,
          created_at: record.created_at,
          domain: record.domain,
          page_url: record.page_url
        }));
        
        console.log('Videos found:', videoFiles);
        setVideos(videoFiles);
      } else {
        const errorText = await response.text();
        console.error('Database error response:', errorText);
        setVideos([]);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
      setVideos([]);
    } finally {
      setLoadingVideos(false);
    }
  };

  // Fetch videos when URL changes
  useEffect(() => {
    if (url) {
      fetchVideos(url);
    }
  }, [url]);

  const handleRecordClick = async () => {
    if (!recording) {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: { mediaSource: 'screen' },
          audio: false
        });
        streamRef.current = stream;
        recordedChunksRef.current = [];
        
        const recorder = new MediaRecorder(stream, {
          mimeType: 'video/webm;codecs=vp9'
        });
        mediaRecorderRef.current = recorder;
        
        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            recordedChunksRef.current.push(event.data);
          }
        };
        
        recorder.onstop = async () => {
          const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
          streamRef.current.getTracks().forEach(track => track.stop());
          
          if (blob.size < 1000) {
            setRecordError('Recording failed: no data captured.');
            return;
          }
          setUploading(true);
          
          try {
            // Create filename using the URL with domain-based folder structure
            const urlObj = new URL(url);
            const domain = urlObj.hostname.replace(/\./g, '-');
            const path = urlObj.pathname.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-');
            const timestamp = Date.now();
            const fileName = `${path}-${timestamp}.webm`;
            const fullPath = `recordings/${domain}/${fileName}`;
            
            console.log('Uploading video to:', fullPath);
            
            // Upload file to storage
            const uploadResponse = await fetch(`${SUPABASE_URL}/storage/v1/object/${SUPABASE_BUCKET}/${fullPath}`, {
            method: 'PUT',
            headers: {
              'apikey': SUPABASE_KEY,
              'Authorization': `Bearer ${SUPABASE_KEY}`,
              'Content-Type': 'video/webm'
            },
            body: blob
          });
            
            if (uploadResponse.ok) {
              // Save metadata to database
              const dbResponse = await fetch(`${SUPABASE_URL}/rest/v1/screen_recordings`, {
                method: 'POST',
                headers: {
                  'apikey': SUPABASE_KEY,
                  'Authorization': `Bearer ${SUPABASE_KEY}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  filename: fileName,
                  file_path: fullPath,
                  domain: domain,
                  page_url: url,
                  file_size: blob.size,
                  created_at: new Date().toISOString()
                })
              });
              
              console.log('Database save response status:', dbResponse.status);
              console.log('Database save response headers:', Object.fromEntries(dbResponse.headers.entries()));
              
              if (dbResponse.ok) {
                console.log('Video metadata saved to database');
          setUploading(false);
          setUploadSuccess(true);
                setUploadedUrl(`${SUPABASE_URL}/storage/v1/object/public/${SUPABASE_BUCKET}/${fullPath}`);
                
                // Refresh videos list
                fetchVideos(url);
              } else {
                const dbError = await dbResponse.text();
                console.error('Database save error status:', dbResponse.status);
                console.error('Database save error response:', dbError);
                console.error('Data being sent:', {
                  filename: fileName,
                  file_path: fullPath,
                  domain: domain,
                  page_url: url,
                  file_size: blob.size,
                  created_at: new Date().toISOString()
                });
                setRecordError('Failed to save video metadata to database: ' + dbError);
              }
            } else {
              const uploadError = await uploadResponse.text();
              console.error('Upload error:', uploadError);
              setRecordError('Failed to upload video to storage');
            }
          } catch (error) {
            console.error('Error during upload:', error);
            setRecordError('Upload failed: ' + error.message);
          } finally {
            setUploading(false);
          }
        };
        recorder.start();
        recordStartTimeRef.current = Date.now();
        setCanStop(false);
        setTimeout(() => setCanStop(true), 800);
        setRecording(true);
      } catch (e) {
        setRecordError('Could not start screen recording: ' + e.message);
      }
    } else {
      // Only allow stop if enough time has passed
      if (canStop && mediaRecorderRef.current) {
        setRecording(false);
        mediaRecorderRef.current.stop();
      }
    }
  };

  return {
    recording,
    canStop,
    uploading,
    uploadSuccess,
    uploadedUrl,
    recordError,
    videos,
    loadingVideos,
    handleRecordClick,
    fetchVideos
  };
}; 